import React, { useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import { ErrorBoundary } from 'react-error-boundary';

import EmptyLoadingState from 'components/empty-loading-state';
import { TwoColumnSidebar } from 'components/layouts/two-column';
import { useStickySidebar } from 'hooks/use-sticky-sidebar';
import useUI from 'hooks/use-ui';
import BrandsFilter from './brands-filter';
import { CategorySubcategoryFilter } from './category-subcategory-filter';
import { CustomTagFilter } from './custom-tag-filter';
import { CustomSmartTagFilter } from './custom-smart-tag-filter';
import { EffectsFilter } from './effects-filter';
import PotencyFilter from './potency-filter';
import PriceFilter from './price-filter';
import { SpecialsFilter } from './specials-filter';
import StrainTypeFilter from './strain-type-filter';
import SubcategoryFilter from './subcategory-filter';
import { SubcategoriesFilterMultiSelect } from './subcategory-filter/subcategory-filter-multi-select';
import { TerpenesFilter } from './terpenes-filter';
import { useFilterSidePanel } from './use-filter-side-panel';
import { useFilterVisibility } from './use-filter-visibility';
import WeightFilter from './weight-filter';
import type { MenuFilterOptions, SidebarCategory, SidebarDispensary } from './types';

type SidebarProps = {
  menuFilterOptions: MenuFilterOptions;
  category?: SidebarCategory | null;
  isLoading: boolean;
  route?: string;
  dispensary?: SidebarDispensary | null;
  onSearchPage?: boolean;
  onCollectionsPage?: boolean;
  customCategory?: unknown;
  customSmartTagNames?: string[];
  customSmartTagTitle?: string;
  customTagNames?: string[];
  customTagTitle?: string;
  globalCategoriesSubcategories?: MenuFilterOptions['categoriesSubcategories'];
};

const FallbackSidebarContext = React.createContext<React.ReactNode>(null);

const FallbackSidebar: React.FC = () => {
  const content = React.useContext(FallbackSidebarContext);
  return <>{content}</>;
};

export default function Sidebar({
  menuFilterOptions,
  category,
  isLoading,
  route,
  dispensary,
  onSearchPage,
  onCollectionsPage,
  customCategory,
  customSmartTagNames = [],
  customSmartTagTitle,
  customTagNames = [],
  customTagTitle,
  globalCategoriesSubcategories,
}: SidebarProps): JSX.Element | null {
  const UI = useUI();
  const isStoreFront = useObserver(() => UI.isStoreFront);
  const { sidebarRef, sidebarStyle } = useStickySidebar({ disableSticky: !isStoreFront });

  const visibility = useFilterVisibility({
    category,
    customCategory,
    customSmartTagNames,
    customTagNames,
    dispensary,
    menuFilterOptions,
    onCollectionsPage,
    onSearchPage,
    route,
  });

  const panelContext = useMemo(
    () => ({
      onBrandsPage: visibility.onBrandsPage,
      onCollectionsPage: Boolean(onCollectionsPage),
      onSearchPage: Boolean(onSearchPage),
      route: route ?? '',
    }),
    [onCollectionsPage, onSearchPage, route, visibility.onBrandsPage]
  );

  const {
    brands,
    categoriesSubcategories,
    subcategories,
    strainTypes,
    weights,
    effects,
    terpenes,
    priceBands,
  } = menuFilterOptions;

  const nativeFilters = (
    <>
      {/*
       * showCategorySubcategoryFilter covers search/collections pages, including custom
       * categories once ecomm.custom-category-subcategory-filters.rollout is enabled for
       * this dispensary/enterprise — see useFilterVisibility for the gating logic.
       */}
      {visibility.showCategorySubcategoryFilter && (
        <CategorySubcategoryFilter categoriesSubcategories={categoriesSubcategories ?? []} />
      )}
      {/*
       * showGlobalCategoryFilter covers category pages (/products/[category]) once
       * ecomm.global-categories-sidebar.rollout is enabled -- it replaces the flat
       * subcategory filter below with the full global category list, expanding the
       * active category's subcategories inline. See PF-9862.
       */}
      {visibility.showGlobalCategoryFilter && (
        <CategorySubcategoryFilter
          categoriesSubcategories={globalCategoriesSubcategories ?? []}
          navigateOnCategoryClick
        />
      )}
      {!customCategory &&
        visibility.showSubcategoryFilter &&
        (visibility.showSubcategoriesFilterMultiSelect ? (
          <SubcategoriesFilterMultiSelect subcategories={subcategories ?? []} />
        ) : (
          <SubcategoryFilter category={category} subcategories={subcategories ?? []} />
        ))}
      {visibility.showSpecialsFilter && <SpecialsFilter />}
      {(priceBands ?? []).length > 0 && <PriceFilter bands={priceBands} />}
      {visibility.showWeights && <WeightFilter weights={weights ?? []} />}
      {visibility.showBrands && <BrandsFilter brands={brands ?? []} />}
      {visibility.showStrainTypes && <StrainTypeFilter strainTypes={strainTypes ?? []} />}
      {visibility.showPotencies && <PotencyFilter category={category} />}
      {visibility.showEffects && <EffectsFilter effects={effects ?? []} />}
      {visibility.showTerpenes && <TerpenesFilter terpenes={terpenes ?? []} />}
      {visibility.showCustomTagFilter && <CustomTagFilter tags={customTagNames} title={customTagTitle} />}
      {visibility.showCustomSmartTagFilter && (
        <CustomSmartTagFilter smartTags={customSmartTagNames} title={customSmartTagTitle} />
      )}
    </>
  );

  const { CustomSidePanel, filterSidePanelProps, sidePanelState } = useFilterSidePanel({
    category,
    context: panelContext,
    fallback: FallbackSidebar,
    globalCategoriesSubcategories,
    isLoading,
    menuFilterOptions,
    visibility,
  });

  if (sidePanelState === 'pending') {
    return null;
  }

  return (
    <FallbackSidebarContext.Provider value={nativeFilters}>
      <TwoColumnSidebar ref={sidebarRef} $stickyStyle={sidebarStyle}>
        <EmptyLoadingState isLoading={isLoading} page='filterSideBar' delay={500}>
          {sidePanelState === 'custom' && CustomSidePanel && filterSidePanelProps ? (
            <ErrorBoundary
              fallbackRender={({ error }) => {
                console.error('Remote FilterSidePanel error:', error);
                return nativeFilters;
              }}
            >
              <CustomSidePanel {...filterSidePanelProps} />
            </ErrorBoundary>
          ) : (
            nativeFilters
          )}
        </EmptyLoadingState>
      </TwoColumnSidebar>
    </FallbackSidebarContext.Provider>
  );
}
